import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { Button, Stack } from '@mui/material';
import {
  allowLeavePage,
  BACK_GUARD_RESTORE_EVENT,
  closeRefreshWarning,
  enableBackNavigationGuard,
  leaveVerificationPage,
  pushGuard,
  registerBackNavigationGuardUi,
  rememberProtectedLocation,
  stayOnVerificationPage,
} from '../../shared/utilities/backNavigationGuard';

const dialogPaperSx = { width: '400px', maxWidth: '70%', height: 'auto' };
const titleStyle = {
  fontFamily: 'Medium',
  color: 'rgb(36 36 75 / 98%)',
  fontSize: '15px',
  fontWeight: 'bold' as const,
  margin: 0,
};
const bodyStyle = {
  fontFamily: 'Medium',
  color: 'rgb(36 36 75 / 98%)',
  fontSize: '16px',
  margin: 0,
};
const buttonStyle = {
  height: '50px',
  borderRadius: '35px',
  marginTop: '10px',
};

/**
 * Mounts refresh/back protection for the KYC SPA session.
 * Re-arms the history trap after each in-app route change.
 */
export default function BackNavigationGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [backOpen, setBackOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const restoringRef = useRef(false);

  useEffect(() => {
    registerBackNavigationGuardUi({
      showBackWarning: () => setBackOpen(true),
      hideBackWarning: () => setBackOpen(false),
      showRefreshWarning: () => setRefreshOpen(true),
      hideRefreshWarning: () => setRefreshOpen(false),
    });
    rememberProtectedLocation(location.pathname, location.search);
    enableBackNavigationGuard();
    pushGuard('init');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Keep protected URL in sync and pad history after each React Router navigation.
  useEffect(() => {
    rememberProtectedLocation(location.pathname, location.search);
    if (allowLeavePage || restoringRef.current) return;
    enableBackNavigationGuard();
    pushGuard('route');
  }, [location.pathname, location.search]);

  // Fallback: if React Router still switched routes, snap back to the protected page.
  useEffect(() => {
    const onRestore = (event: Event) => {
      const detail = (event as CustomEvent<{ pathname: string; search: string }>).detail;
      if (!detail) return;
      if (location.pathname === detail.pathname && location.search === detail.search) {
        return;
      }
      restoringRef.current = true;
      navigate(`${detail.pathname}${detail.search}`, { replace: true });
      window.setTimeout(() => {
        restoringRef.current = false;
        rememberProtectedLocation(detail.pathname, detail.search);
        pushGuard('restore');
      }, 0);
    };

    window.addEventListener(BACK_GUARD_RESTORE_EVENT, onRestore);
    return () => window.removeEventListener(BACK_GUARD_RESTORE_EVENT, onRestore);
  }, [location.pathname, location.search, navigate]);

  const handleStay = () => {
    stayOnVerificationPage();
  };

  const handleLeave = () => {
    leaveVerificationPage();
  };

  const handleCloseRefresh = () => {
    closeRefreshWarning();
  };

  return (
    <>
      <Dialog open={backOpen} PaperProps={{ sx: dialogPaperSx }} aria-labelledby="back-nav-warning-title">
        <DialogContent style={{ textAlign: 'center', overflow: 'hidden' }}>
          <DialogContentText id="back-nav-warning-title" style={{ margin: '0 auto', display: 'table' }}>
            <p style={titleStyle}>هشدار خروج</p>
          </DialogContentText>
          <DialogContentText mt={3}>
            <p style={bodyStyle}>
              در صورت بازگشت، فرآیند احراز هویت ناتمام می‌ماند و ممکن است امکان ادامه وجود نداشته باشد. آیا می‌خواهید
              خارج شوید؟
            </p>
          </DialogContentText>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            <Button style={buttonStyle} onClick={handleStay} fullWidth variant="contained">
              <span style={{ fontSize: 17, fontFamily: 'Medium, sans-serif' }}>ماندن</span>
            </Button>
            <Button style={buttonStyle} onClick={handleLeave} fullWidth variant="outlined" color="error">
              <span style={{ fontSize: 17, fontFamily: 'Medium, sans-serif' }}>خروج</span>
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={refreshOpen} PaperProps={{ sx: dialogPaperSx }} aria-labelledby="refresh-warning-title">
        <DialogContent style={{ textAlign: 'center', overflow: 'hidden' }}>
          <DialogContentText id="refresh-warning-title" style={{ margin: '0 auto', display: 'table' }}>
            <p style={titleStyle}>هشدار بارگذاری مجدد</p>
          </DialogContentText>
          <DialogContentText mt={3}>
            <p style={bodyStyle}>
              بارگذاری مجدد این صفحه باعث قطع فرآیند می‌شود و امکان ادامه وجود ندارد.
            </p>
          </DialogContentText>
          <Button style={buttonStyle} onClick={handleCloseRefresh} fullWidth variant="contained">
            <span style={{ fontSize: 17, fontFamily: 'Medium, sans-serif' }}>متوجه شدم</span>
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
